import { Material } from "three";

type NodeMaterialLike = Material & {
    isNodeMaterial: true;
}
export function isNodeMaterial(obj: unknown): obj is NodeMaterialLike {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const material = obj as NodeMaterialLike;
    return material.isNodeMaterial === true;
}

export function ignoreNodeMaterialProperty(material: NodeMaterialLike, propertyKey: string): boolean {
    // In node materials some keys exist but aren't used anymore
    // @TODO: We might to check here for the material type in the future
    switch (propertyKey) {
        case "color":
        case "metalness":
        case "roughness":
        case "emissive":
        case "emissiveIntensity":
            return true;
    }

    // Ignore TSL node properties - they're handled by TSL property extraction
    // These properties end with "Node" and contain TSL node graphs
    if (propertyKey.endsWith("Node")) {
        const value = (material as any)[propertyKey];
        if (isNodeLike(value)) {
            return true;
        }
    }

    return false;
}

export interface NodeLike {
    readonly isNode: true;
    name: string;
    needsUpdate: boolean;
    readonly uuid: string;
    readonly version: number;
    /** The node type. This represents the result type of the node (e.g. float or vec3). */
    nodeType: string | null;
    /** The type of the class. The value is usually the constructor name. */
    type: string;
}

export function isNodeLike(obj: unknown): obj is NodeLike {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const node = obj as NodeLike;
    return node.isNode === true && typeof node.uuid === "string"
        && typeof node.uuid === "string" && typeof node.version === "number";
}

export interface InputNodeLike extends NodeLike {
    readonly isInputNode: true;
    precision: "lowp" | "mediump" | "highp" | null;
    value: any;
}

export function isInputNode(obj: unknown): obj is InputNodeLike {
    if (!isNodeLike(obj)) return false;
    const node = obj as InputNodeLike;
    return node.isInputNode === true && 'value' in node;
}

export interface VarNodeLike extends NodeLike {
    readonly isVarNode: true;
    intent: boolean;
    global: boolean;
    node: NodeLike;
    readOnly: boolean;
    parents: boolean;
}
export function isVarNode(obj: unknown): obj is VarNodeLike {
    if (!isNodeLike(obj)) return false;
    const node = obj as VarNodeLike;
    return node.isVarNode === true && typeof node.intent === "boolean" && typeof node.global === "boolean" && isNodeLike(node.node) && typeof node.readOnly === "boolean" && typeof node.parents === "boolean";
}

/**
 * Represents a uniform node that can be updated per frame/render/object
 */
export interface UniformNodeLike extends InputNodeLike {
    readonly isUniformNode: true;
}

export function isUniformNode(obj: unknown): obj is UniformNodeLike {
    if (!isInputNode(obj)) return false;
    const node = obj as UniformNodeLike;
    return node.isUniformNode === true;
}

/**
 * Represents a constant node that holds an immutable value
 */
export interface ConstNodeLike extends InputNodeLike {
    readonly isConstNode: true;
}

export function isConstNode(obj: unknown): obj is ConstNodeLike {
    if (!isInputNode(obj)) return false;
    const node = obj as ConstNodeLike;
    return node.isConstNode === true;
}

/**
 * Represents an attribute node that reads from geometry attributes
 */
export interface AttributeNodeLike extends NodeLike {
    readonly isAttributeNode: true;
    attributeName: string;
}

export function isAttributeNode(obj: unknown): obj is AttributeNodeLike {
    if (!isNodeLike(obj)) return false;
    const node = obj as AttributeNodeLike;
    return node.isAttributeNode === true && typeof node.attributeName === "string";
}

/**
 * Represents a varying node for passing data between vertex and fragment shaders
 */
export interface VaryingNodeLike extends NodeLike {
    readonly isVaryingNode: true;
    node: NodeLike;
}

export function isVaryingNode(obj: unknown): obj is VaryingNodeLike {
    if (!isNodeLike(obj)) return false;
    const node = obj as VaryingNodeLike;
    return node.isVaryingNode === true && isNodeLike(node.node);
}

/**
 * Represents a texture sampling node
 */
export interface TextureNodeLike extends NodeLike {
    readonly isTextureNode: true;
    value: unknown;
    uvNode: NodeLike | null;
}

export function isTextureNode(obj: unknown): obj is TextureNodeLike {
    if (!isNodeLike(obj)) return false;
    const node = obj as TextureNodeLike;
    return node.isTextureNode === true && "value" in node;
}

/**
 * Represents an operator node (math operations, etc.)
 */
export interface OperatorNodeLike extends NodeLike {
    readonly isOperatorNode: true;
    op: string;
    aNode: NodeLike;
    bNode: NodeLike | null;
}

export function isOperatorNode(obj: unknown): obj is OperatorNodeLike {
    if (!isNodeLike(obj)) return false;
    const node = obj as OperatorNodeLike;
    return node.isOperatorNode === true && typeof node.op === "string" && isNodeLike(node.aNode);
}

/**
 * Represents a function node that can execute custom shader functions
 */
export interface FunctionNodeLike extends NodeLike {
    readonly isFunctionNode: true;
}

export function isFunctionNode(obj: unknown): obj is FunctionNodeLike {
    if (!isNodeLike(obj)) return false;
    const node = obj as FunctionNodeLike;
    return node.isFunctionNode === true;
}